/**
 * Pursuit system: steers units with an attack order to a free tile within
 * range of their target, re-planning only when the target leaves range,
 * following up on a nearby visible enemy when it dies (aggressive units only), and dropping the
 * order once a rival unit is out of sight (no fog leak).
 */
import { getHexTileMapKey } from '../../hexmap/model/keys';
import { hexesWithinRadius, isPassable } from '../../hexmap/model/map';
import type { HexTileMapKey } from '../../hexmap/model/types';
import {
  findPathToNearest,
  nearestReachable,
  pathTo,
  reachableFrom,
} from '../../hexmap/pathfinding';
import { tickDerived } from '../derived';
import { buildingCells, distanceTo } from '../footprint';
import { stopKind } from '../occupancy';
import { redirect } from '../redirect';
import { isHidden } from '../spies';
import type { Building, Cell, EntityId, SimContext, SimState, SystemFn, Unit } from '../types';
import { shelteredUnits } from './combat';

/** Whether a unit of this type fights (attack action and some strength). */
function canAttack(rules: SimContext['rules'], unit: Unit): boolean {
  const def = rules.units[unit.type];
  return !!def && def.actions.includes('attack') && def.strength > 0;
}

/**
 * Whether a unit of this type may hit a unit (`targetIsUnit`) or a building,
 * per its rules `targets` (omitted = any).
 */
function canHit(rules: SimContext['rules'], unitType: string, targetIsUnit: boolean): boolean {
  const targets = rules.units[unitType]?.targets ?? 'any';
  return targets === 'any' || (targets === 'units') === targetIsUnit;
}

/**
 * The entity an attack order points at: an enemy unit or building. A unit
 * inside a building or hidden (an unexposed spy, spies.ts) can't be targeted.
 */
function attackTargetOf(
  state: SimState,
  rules: SimContext['rules'],
  targetId: EntityId
): Unit | Building | undefined {
  const unit = state.units[targetId];
  if (unit) return unit.inside || isHidden(rules, unit) ? undefined : unit;
  return state.buildings[targetId];
}

/** Every tile within `range` of any of the target's tiles (duplicates possible). */
function tilesInRange(target: Unit | Building, range: number): Cell[] {
  const anchors = 'footprint' in target ? buildingCells(target) : [target.pos];
  return anchors.flatMap((c) => hexesWithinRadius(c, range));
}

function idNumber(id: EntityId): number {
  return Number.parseInt(id.slice(1), 10);
}

/**
 * A pursuer finding no free tile in range of its target stages on a free
 * tile up to this many hexes further out (range + 1 or + 2)…
 */
const STAGE_RINGS = 2;
/** …and looks again after this many ticks, in its own phase (by id). */
const STAGE_RETRY_TICKS = 5;
/**
 * The staging search gives up after reaching this many tiles in range, or
 * every tile in range it could enter if fewer (6 at most for melee)…
 */
const STAGE_SEARCH_LIMIT = 12;
/**
 * …or after this many more tiles past the first in range, a backstop for
 * tiles in range it can't get to (walled in), so a crowded target never
 * costs a search of the whole map.
 */
const STAGE_SEARCH_CELLS = 64;

/**
 * The tick a staged pursuer next looks for a free tile in range: at least
 * STAGE_RETRY_TICKS from now, in its own phase by id, so a crowd staged on
 * one tick doesn't all search again on one tick.
 */
function stageRetryAt(state: SimState, unit: Unit): number {
  const at = state.tick + STAGE_RETRY_TICKS;
  const phase = (idNumber(unit.id) || 0) % STAGE_RETRY_TICKS;
  return at + ((((phase - at) % STAGE_RETRY_TICKS) + STAGE_RETRY_TICKS) % STAGE_RETRY_TICKS);
}

/** How far (hexes) a unit looks for its next target after its target dies. */
const FOLLOW_UP_RADIUS = 4;

/**
 * The follow-up target of a manual fighter whose target is gone (died, or
 * went inside a building), or that an attack-move meets on its way: the
 * nearest enemy unit (outside buildings and not sheltered in its city's
 * territory, so it can be hit) or building within `radius` (FOLLOW_UP_RADIUS,
 * or a longer-ranged unit's range) that it may hit and its owner can see
 * (ties to the lowest id); undefined if none. `unitsFirst` (an attack-move,
 * so a column doesn't divert to demolish every building it passes) considers
 * buildings only when no unit is in reach.
 */
function followUpTarget(
  state: SimState,
  rules: SimContext['rules'],
  unit: Unit,
  vision: ReadonlySet<HexTileMapKey>,
  sheltered: ReadonlySet<EntityId>,
  radius = FOLLOW_UP_RADIUS,
  unitsFirst = false
): Unit | Building | undefined {
  let best: Unit | Building | undefined;
  let bestDist = Number.POSITIVE_INFINITY;
  // Cheap checks first: this runs per attack-mover per tick, so the vision lookup waits.
  const consider = (target: Unit | Building, dist: number, seen: () => boolean) => {
    if (target.owner === unit.owner || dist > radius || dist > bestDist) return;
    if (!canHit(rules, unit.type, target.id in state.units) || !seen()) return;
    const tie = dist === bestDist && best && idNumber(target.id) < idNumber(best.id);
    if (dist < bestDist || tie) {
      best = target;
      bestDist = dist;
    }
  };
  for (const u of Object.values(state.units)) {
    if (u.inside || sheltered.has(u.id) || isHidden(rules, u)) continue;
    consider(u, distanceTo(u, unit.pos), () => vision.has(getHexTileMapKey(u.pos)));
  }
  if (unitsFirst && best) return best;
  for (const b of Object.values(state.buildings)) {
    consider(b, distanceTo(b, unit.pos), () =>
      buildingCells(b).some((c) => vision.has(getHexTileMapKey(c)))
    );
  }
  return best;
}

/**
 * The target `unit` was sent at before it turned on a blocker (the order's
 * `resume`), while it's still there to go back to: an enemy building, or an
 * enemy unit its owner can see, that it may hit. Undefined otherwise.
 */
function resumeTarget(
  state: SimState,
  rules: SimContext['rules'],
  unit: Unit,
  vision: ReadonlySet<HexTileMapKey>
): Unit | Building | undefined {
  const id = unit.order?.kind === 'attack' ? unit.order.resume : undefined;
  if (id === undefined) return undefined;
  const target = attackTargetOf(state, rules, id);
  if (!target || target.owner === unit.owner) return undefined;
  const isUnit = target.id in state.units;
  if (!canHit(rules, unit.type, isUnit)) return undefined;
  if (isUnit && !vision.has(getHexTileMapKey(target.pos))) return undefined;
  return target;
}

/** Most targets a focus queue holds after the current one (extras are ignored). */
const MAX_FOCUS_QUEUE = 8;

/**
 * The next valid target of `unit`'s focus queue (`order.queue`), dropping
 * entries on the way that can never be taken: gone, own, inside a building
 * or hidden, not hittable by this unit's type. A rival unit the owner can't
 * see right now is skipped but kept (it may step back into sight; taking it
 * would leak fog). Returns the pick with the rest of the queue (the skipped
 * ones first); on a miss the queue shrinks to the skipped ones and the
 * result is undefined: the queue only goes with the order.
 */
function nextQueued(
  state: SimState,
  rules: SimContext['rules'],
  unit: Unit,
  vision: ReadonlySet<HexTileMapKey>
): { target: Unit | Building; rest: EntityId[] } | undefined {
  const order = unit.order?.kind === 'attack' ? unit.order : undefined;
  if (!order?.queue) return undefined;
  const queue = order.queue;
  const unseen: EntityId[] = [];
  for (let i = 0; i < queue.length; i++) {
    const target = attackTargetOf(state, rules, queue[i]);
    if (!target || target.owner === unit.owner) continue;
    const isUnit = target.id in state.units;
    if (!canHit(rules, unit.type, isUnit)) continue;
    if (isUnit && !vision.has(getHexTileMapKey(target.pos))) {
      unseen.push(target.id);
      continue;
    }
    return { target, rest: [...unseen, ...queue.slice(i + 1)] };
  }
  if (unseen.length > 0) order.queue = unseen;
  else delete order.queue;
  return undefined;
}

/**
 * Takes the next valid queued target (`nextQueued`) as `unit`'s current one,
 * carrying the rest of the queue, the guard tag and the attack-move
 * destination; undefined (order untouched but for an emptied queue) when
 * none is left.
 */
function advanceQueue(
  state: SimState,
  rules: SimContext['rules'],
  unit: Unit,
  vision: ReadonlySet<HexTileMapKey>
): Unit | Building | undefined {
  const prev = unit.order?.kind === 'attack' ? unit.order : undefined;
  const next = nextQueued(state, rules, unit, vision);
  if (!prev || !next) return undefined;
  unit.order = { kind: 'attack', targetId: next.target.id };
  // A new engagement of its own: no longer kept to the slowest of the units it was sent with.
  delete unit.pace;
  if (prev.guard !== undefined) unit.order.guard = prev.guard;
  if (prev.onward) unit.order.onward = prev.onward;
  if (next.rest.length > 0) unit.order.queue = next.rest;
  return next.target;
}

/**
 * Turns `unit`'s attack order onto `target`, keeping its guard tag, focus
 * queue and attack-move destination (a resumed order has no `resume`) but
 * not the shared pace (`Unit.pace`).
 */
function retarget(unit: Unit, target: Unit | Building): void {
  const prev = unit.order?.kind === 'attack' ? unit.order : undefined;
  unit.order = { kind: 'attack', targetId: target.id };
  delete unit.pace;
  if (prev?.guard !== undefined) unit.order.guard = prev.guard;
  if (prev?.onward) unit.order.onward = prev.onward;
  if (prev?.queue) unit.order.queue = prev.queue;
}

/**
 * A fight is over for `unit` (nothing left to follow up on, or the target was
 * lost): an attack-move walks on to its destination `onward` (`kind`
 * 'attackMove' keeps meeting enemies; 'move' just walks, for a target it
 * couldn't reach so the unit doesn't pick it again), anything else goes
 * idle. The route is planned round what the owner can see, to the nearest
 * reachable tile of the destination, through redirect(); the shared pace
 * isn't restored (it walks on at its own speed), and the group formation
 * pulls it back in as it does any mover.
 */
function endFight(
  state: SimState,
  ctx: SimContext,
  unit: Unit,
  blockers: ReadonlySet<HexTileMapKey>,
  kind: 'attackMove' | 'move'
): void {
  const onward = unit.order?.kind === 'attack' ? unit.order.onward : undefined;
  delete unit.order; // and with it any focus queue
  if (!onward) return;
  const terrain = ctx.rules.units[unit.type]?.terrain ?? [];
  const reach = reachableFrom(ctx.terrain, unit.pos, terrain, blockers);
  const goal = nearestReachable(reach, onward);
  const path = goal ? pathTo(reach, goal) : [];
  if (path.length <= 1) {
    // Nowhere to walk on to: drop the stale pursuit path (keeping the step in flight).
    redirect(state, unit, [unit.pos]);
    return;
  }
  unit.order = { kind: unit.stance ? 'move' : kind, target: { q: onward.q, r: onward.r } };
  redirect(state, unit, path);
}

/**
 * Units with an attack order close in on their target: each heads for the
 * nearest free tile within its `range` of the target (by walking distance,
 * then breadth-first order), stops once it's in range, and re-plans when the
 * target moves out of range of the tile it's heading for. "Free" means no
 * other own unit is standing there (including pursuers already in range) or
 * heading there this tick; units are handled in id order so the result is
 * deterministic. Tiles where the unit can't stop (another unit standing or
 * heading there, a building that doesn't take it; see occupancy.ts) are
 * skipped too, and it doesn't stop in range on one. When the target is
 * gone (died or went inside), the next valid target of its focus queue
 * (`order.queue`, see `nextQueued`) is taken first; with none, a manual, aggressive unit follows up on the nearest visible enemy within
 * FOLLOW_UP_RADIUS (see `followUpTarget`), else the order is dropped (or, in an
 * attack-move, the walk to its destination resumes: `endFight`). Units on an
 * attack-move order (`attackMove`) first look for an enemy to engage within
 * max(FOLLOW_UP_RADIUS, their range) and turn on the nearest, keeping the
 * destination in `onward`; a stance unit never diverts (it walks on and
 * fires at what's in range, combat.ts). A unit target
 * is only tracked while its owner's side can see it: out of sight the pursuer
 * finishes its current path, then gives up, so pursuit never leaks fog. Runs before movement, so a new path advances the
 * same tick; combat then hits the ordered target first (see combat.ts).
 */
const pursuit: SystemFn = (state, ctx) => {
  const pursuers: Unit[] = [];
  const movers: Unit[] = [];
  for (const u of Object.values(state.units)) {
    if (u.order?.kind === 'attack') pursuers.push(u);
    else if (u.order?.kind === 'attackMove') movers.push(u);
  }
  if (pursuers.length === 0 && movers.length === 0) return;
  const derived = tickDerived(state, ctx);
  const visionOf = derived.vision;
  let sheltered: Set<EntityId> | undefined;

  // Attack-movers meet enemies: the nearest visible one in reach becomes an
  // attack order (the destination kept in `onward`), planned below this tick.
  movers.sort((a, b) => idNumber(a.id) - idNumber(b.id));
  for (const unit of movers) {
    if (unit.order?.kind !== 'attackMove') continue;
    // Arrived (movement drops the order at the end of a path): nothing to meet on the way.
    if (!unit.path) {
      delete unit.order;
      continue;
    }
    const def = ctx.rules.units[unit.type];
    if (!def || unit.stance || unit.auto || unit.inside) continue;
    sheltered ??= shelteredUnits(state, ctx);
    const radius = Math.max(FOLLOW_UP_RADIUS, def.range);
    const found = followUpTarget(
      state,
      ctx.rules,
      unit,
      visionOf(unit.owner),
      sheltered,
      radius,
      true
    );
    if (!found) continue;
    unit.order = { kind: 'attack', targetId: found.id, onward: unit.order.target };
    // A new engagement of its own: no longer kept to the slowest of the units it was sent with.
    delete unit.pace;
    pursuers.push(unit);
  }
  pursuers.sort((a, b) => idNumber(a.id) - idNumber(b.id));
  if (pursuers.length === 0) return;

  // Tiles taken per owner, with who holds each: standing units, pursuers
  // already stopped in range of their target (so one passing through doesn't
  // stop on the same tile), and tiles earlier pursuers picked.
  const taken = new Map<string, Map<HexTileMapKey, EntityId>>();
  const takenBy = (owner: string) => {
    let tiles = taken.get(owner);
    if (!tiles) {
      tiles = new Map();
      for (const u of Object.values(state.units)) {
        // A hidden unit (spies.ts) holds no tile.
        if (u.owner !== owner || u.order?.kind === 'attack' || isHidden(ctx.rules, u)) continue;
        if (!u.path || u.path.length === 0) tiles.set(getHexTileMapKey(u.pos), u.id);
      }
      for (const u of pursuers) {
        if (u.owner !== owner || u.order?.kind !== 'attack' || u.path) continue;
        const target = attackTargetOf(state, ctx.rules, u.order.targetId);
        const range = ctx.rules.units[u.type]?.range ?? 1;
        const key = getHexTileMapKey(u.pos);
        if (target && distanceTo(target, u.pos) <= range && !tiles.has(key)) {
          tiles.set(key, u.id);
        }
      }
      taken.set(owner, tiles);
    }
    return tiles;
  };
  // As the pass starts (pursuers' new paths don't count).
  const occ = derived.occupancy();
  // Fighting ground: open tiles (a building would take the unit inside).
  const canStand = (unit: Unit, cell: Cell, reserve: boolean) =>
    stopKind(ctx.rules, occ, unit, cell, reserve) === 'open';
  // Paths go around what the owner can see in the way (fog-safe).
  const blockersOf = derived.blockers;

  for (const unit of pursuers) {
    if (unit.order?.kind !== 'attack') continue;
    let target = attackTargetOf(state, ctx.rules, unit.order.targetId);
    const def = ctx.rules.units[unit.type];
    // A blocker it turned on is gone: back to the target it was sent at
    // (ahead of the focus queue: the blocker was never part of the focus).
    if (!target && def) {
      target = resumeTarget(state, ctx.rules, unit, visionOf(unit.owner));
      if (target) retarget(unit, target);
    }
    // The focus queue comes before the fallbacks below: the target is gone
    // (died, went inside) or has become own (a captured building).
    if ((!target || target.owner === unit.owner) && def && unit.order.queue) {
      target = advanceQueue(state, ctx.rules, unit, visionOf(unit.owner)) ?? target;
    }
    // Follow-up: a manual fighter whose target died (or went inside) takes
    // the nearest visible enemy within FOLLOW_UP_RADIUS, else goes idle.
    if (!target && def && !unit.auto && !unit.stance) {
      sheltered ??= shelteredUnits(state, ctx);
      target = followUpTarget(state, ctx.rules, unit, visionOf(unit.owner), sheltered);
      if (target) {
        const onward = unit.order.onward;
        unit.order = { kind: 'attack', targetId: target.id };
        if (onward) unit.order.onward = onward;
        // A new engagement of its own: no longer kept to the slowest of the units it was sent with.
        delete unit.pace;
      }
    }
    if (!target || target.owner === unit.owner || !def) {
      endFight(state, ctx, unit, blockersOf(unit.owner), 'attackMove');
      continue;
    }
    const range = def.range;
    const occupied = takenBy(unit.owner);

    // In range: stop and fight, unless another pursuer already holds this
    // tile (e.g. passing through it on the way to a free one).
    const here = getHexTileMapKey(unit.pos);
    const holder = occupied.get(here);
    const heldByPursuer = holder !== undefined && state.units[holder]?.order?.kind === 'attack';
    if (
      distanceTo(target, unit.pos) <= range &&
      (!heldByPursuer || holder === unit.id) &&
      canStand(unit, unit.pos, false)
    ) {
      // Holds the tile it ends on: a step past halfway is finished first.
      occupied.set(getHexTileMapKey(redirect(state, unit, [unit.pos])), unit.id);
      continue;
    }
    // A rival unit out of sight: finish the current path, then give up
    // (going back to the target it was sent at, if it had turned on this one).
    if (target.id in state.units && !visionOf(unit.owner).has(getHexTileMapKey(target.pos))) {
      // As for a gone target: back to the one it was sent at, then the focus
      // queue (a visible entry is taken at once, the path needn't finish).
      const vision = visionOf(unit.owner);
      const back = resumeTarget(state, ctx.rules, unit, vision);
      if (back && !unit.order.queue && unit.path) continue;
      if (back) retarget(unit, back);
      const next = back ?? advanceQueue(state, ctx.rules, unit, vision);
      if (!next) {
        if (unit.path) continue;
        endFight(state, ctx, unit, blockersOf(unit.owner), 'attackMove');
        continue;
      }
      target = next;
    }
    // A guard that found nothing in range reachable: stand until the target moves.
    if (unit.order.stuckAt === getHexTileMapKey(target.pos)) continue;
    // Staged near a crowded target (see below): hold there until its retry
    // tick, unless the target moved so far that the stage is no longer near.
    if (unit.order.retryAt !== undefined) {
      const stage = unit.path?.[unit.path.length - 1] ?? unit.pos;
      const stageKey = getHexTileMapKey(stage);
      if (
        state.tick < unit.order.retryAt &&
        distanceTo(target, stage) <= range + STAGE_RINGS &&
        !occupied.has(stageKey)
      ) {
        occupied.set(stageKey, unit.id);
        continue;
      }
      delete unit.order.retryAt;
    }
    // Still heading for a free tile in range: keep going.
    const end = unit.path?.[unit.path.length - 1];
    if (
      end &&
      distanceTo(target, end) <= range &&
      !occupied.has(getHexTileMapKey(end)) &&
      canStand(unit, end, true)
    ) {
      occupied.set(getHexTileMapKey(end), unit.id);
      continue;
    }

    // The pick: the first tile in range the search reaches that's free,
    // else the first in range at all. Never the target's own tile: fight
    // from beside it.
    const goal = target;
    const inRange = (cell: Cell) => {
      const dist = distanceTo(goal, cell);
      return dist > 0 && dist <= range;
    };
    const isFree = (cell: Cell) =>
      !occupied.has(getHexTileMapKey(cell)) && canStand(unit, cell, true);
    // When no tile in range is free at all, the unit stages just outside it
    // instead: the nearest free tile within STAGE_RINGS of range, held until
    // a staggered retry tick, rather than walking onto a taken tile in range
    // and searching again every tick.
    const anyFree = tilesInRange(goal, range).some(
      (cell) => ctx.terrain.has(getHexTileMapKey(cell)) && inRange(cell) && isFree(cell)
    );
    // Tiles in range the search could enter: the most it can reach.
    const blockers = blockersOf(unit.owner);
    const enterable = new Set<HexTileMapKey>();
    if (!anyFree) {
      for (const cell of tilesInRange(goal, range)) {
        const key = getHexTileMapKey(cell);
        const tile = ctx.terrain.get(key);
        if (tile && inRange(cell) && isPassable(tile, def.terrain) && !blockers.has(key)) {
          enterable.add(key);
        }
      }
    }
    const inRangeLimit = Math.max(1, Math.min(STAGE_SEARCH_LIMIT, enterable.size));
    let free: Cell | undefined;
    let any: Cell | undefined;
    let inRangeSeen = 0;
    let pastCrowd = 0;
    const reach = reachableFrom(ctx.terrain, unit.pos, def.terrain, blockers, (cell) => {
      if (anyFree) {
        if (!inRange(cell)) return false;
        if (!isFree(cell)) return false;
        free = cell;
        return true;
      }
      if (inRange(cell)) {
        any ??= cell;
        // Reached the crowd: stop once a stage is known too, or after a good look round.
        return free !== undefined || ++inRangeSeen >= inRangeLimit;
      }
      if (free === undefined && distanceTo(goal, cell) <= range + STAGE_RINGS && isFree(cell)) {
        free = cell;
        return any !== undefined;
      }
      return any !== undefined && ++pastCrowd >= STAGE_SEARCH_CELLS;
    });
    delete unit.order.stuckAt;
    // Stage only beside a crowd the unit can reach: a target ringed by
    // rivals, rival footprints or water is the out-of-reach case below. With
    // no stage free, walk at a taken tile in range (no hold: it can't stand
    // there, so it looks again next tick, a bounded search).
    const pick = anyFree ? free : any !== undefined ? (free ?? any) : undefined;
    if (pick) {
      if (!anyFree && free) unit.order.retryAt = stageRetryAt(state, unit);
      redirect(state, unit, pathTo(reach, pick));
      occupied.set(getHexTileMapKey(pick), unit.id);
      continue;
    }
    // Nothing in range is reachable (e.g. across water): get as close as
    // possible, and give up once there (a guard keeps its order and stands,
    // so guard.ts doesn't hand it the same target every tick).
    if (unit.path) continue;
    const path = findPathToNearest(
      ctx.terrain,
      unit.pos,
      target.pos,
      def.terrain,
      blockersOf(unit.owner)
    );
    if (path && path.length > 1) redirect(state, unit, path);
    else if (unit.order.guard !== undefined) unit.order.stuckAt = getHexTileMapKey(target.pos);
    else endFight(state, ctx, unit, blockersOf(unit.owner), 'move');
  }
};

export {
  advanceQueue,
  attackTargetOf,
  canAttack,
  canHit,
  endFight,
  FOLLOW_UP_RADIUS,
  MAX_FOCUS_QUEUE,
  pursuit,
};
